import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useCompanies } from '@/lib/query/hooks';
import type { CompanyWithStats } from '@/lib/types/mise/company';

export function CompaniesPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');

  // 企業一覧を取得（検索キーワードが変更されると自動的に再取得）
  const {
    data: companies = [],
    isLoading: companiesLoading,
    error: companiesError,
  } = useCompanies(searchTerm || undefined);

  const handleCompanyClick = (companyId: string) => {
    navigate(`/mise/companies/${companyId}`);
  };

  // 検索機能（検索キーワードを更新するとuseCompaniesが自動的に再取得）
  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  // 新規登録ボタンのハンドラー
  const handleNewCompany = () => {
    navigate('/mise/companies/new');
  };

  // ローディング状態
  const loading = companiesLoading;
  // エラー状態
  const error = companiesError;

  // 認証ローディング中
  if (authLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>認証状態を確認中...</Typography>
      </Box>
    );
  }

  // 未認証の場合（ミドルウェアでリダイレクトされるため、ここには到達しないはず）
  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>サインインページにリダイレクト中...</Typography>
      </Box>
    );
  }

  return (
    <>
      {loading ? (
        <AppLayout showSearch={false}>
          <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
          </Box>
        </AppLayout>
      ) : error ? (
        <AppLayout showSearch={false}>
          <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
            <Alert severity="error">
              {error instanceof Error ? error.message : '企業データの読み込みに失敗しました。'}
            </Alert>
          </Box>
        </AppLayout>
      ) : (
        <AppLayout onSearch={handleSearch}>
          <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h4" component="h1" gutterBottom>
                企業一覧 ({companies.length}件)
              </Typography>
              <Button variant="contained" startIcon={<Plus size={20} />} onClick={handleNewCompany}>
                新規登録
              </Button>
            </Box>
            <CompanyTable companies={companies} onCompanyClick={handleCompanyClick} />
          </Box>
        </AppLayout>
      )}
    </>
  );
}

interface CompanyTableProps {
  companies: CompanyWithStats[];
  onCompanyClick?: (companyId: string) => void;
}

function CompanyTable({ companies, onCompanyClick }: CompanyTableProps) {
  const handleRowClick = (companyId: string) => {
    onCompanyClick?.(companyId);
  };

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <TableContainer
        sx={{
          overflowX: 'auto',
          maxWidth: '100%',
          '& .MuiTable-root': {
            minWidth: 650,
          },
        }}
      >
        <Table sx={{ minWidth: 650, whiteSpace: 'nowrap' }} aria-label="企業テーブル">
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>企業ID</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                アカウント数
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                ユーザー数
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="text.secondary">企業が見つかりませんでした</Typography>
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow
                  key={company.company_id}
                  hover
                  onClick={() => handleRowClick(company.company_id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{company.company_id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                    {company.account_count}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                    {company.user_count}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
